import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MediaQuery from "react-responsive";
import {
  pathogenicColor,
  benignColor,
} from "../../../../common/helpers/colors";
import {
  getVarsoUrl,
  getAlleleFrequency,
  renderMainDataPoint,
} from "../../../../common/helpers/helpers";
import { bravoListSelector } from "../../../../state/selectors/Bravo.selectors";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import {
  getAlt,
  getRef,
  getChromosome,
  getPosition,
  getGenome,
} from "../../../../state/selectors/general.selectors";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import HelperText from "../../../../components/atoms/HelperText";

const FILTERS = [
  {
    id: "PASS",
    description: "All filters passed",
  },
  {
    id: "CEN",
    description:
      "Variant located in centromeric region with inferred sequences",
  },
  {
    id: "SVM",
    description: "Variant failed SVM filter",
  },
  {
    id: "DISC",
    description:
      "Mendelian or duplicate genotype discordance is high (5% or more)",
  },
  {
    id: "CHRXHET",
    description: "Excess heterozygosity in chrX in males",
  },
  {
    id: "EXHET",
    description: "Excess heterozygosity with HWE p-value < 1e-6",
  },
];

const getTableHeaders = () => {
  return (
    <thead>
      <tr>
        <th>Allele Count</th>
        <th>Allele Number</th>
        <th>Homozygotes</th>
        <th>Allele Frequency</th>
      </tr>
    </thead>
  );
};

const getFrequencyColor = (frequency) => {
  let freq;
  let color;
  let weight;
  if (frequency) {
    freq = frequency * 100;
    if (freq > 5) {
      color = benignColor;
      weight = "bold";
    } else if (freq < 0.01) {
      color = pathogenicColor;
      weight = "bold";
    }
  }
  return { color, weight };
};

const getFilters = (filters) => {
  let desc;
  const rows = [];
  for (let i = 0; i < filters.length; i++) {
    let color;
    let weight;
    if (filters[i] === "SVM") {
      color = pathogenicColor;
      weight = "bold";
    }

    desc = FILTERS.filter((obj) => obj.id === filters[i]);

    rows.push(
      <tr key={i}>
        <td>{i === 0 ? <b>Filter:</b> : ""}</td>
        <td
          style={{
            padding: "2px",
            color,
            fontWeight: weight,
          }}
        >
          {`${filters[i]} - ${desc[0].description}`}
        </td>
      </tr>
    );
  }

  return (
    <table data-testid="filters-table">
      <tbody>{rows}</tbody>
    </table>
  );
};

const getBravoUrl = (refGenome) => {
  let bravoUrl;
  if (refGenome?.name === "hg19") {
    bravoUrl = `https://bravo.sph.umich.edu/freeze3a/hg19/`;
  } else {
    bravoUrl = `https://bravo.sph.umich.edu/freeze8/hg38/`;
  }
  return bravoUrl;
};
class Bravo extends React.Component {
  getViewBravoUrl() {
    const { bravo, reference, alternate, genome, position, chromosome } =
      this.props;
    const bravoUrl = getBravoUrl(genome);
    let viewBravoUrl;
    let tmpl;
    const ov = bravo[0].original_variant;
    let snv;
    if (genome) {
      snv = genome.name === "hg19" ? "" : "snv/";
    }
    if (ov && ov != null) {
      viewBravoUrl = `${bravoUrl}variant/${snv}${ov}`;
    } else if (
      reference &&
      alternate &&
      reference.length === 1 &&
      alternate.length === 1
    ) {
      const chr = chromosome.replace("chr", "");
      viewBravoUrl = `${bravoUrl}variant/${snv}${chr}-${position}-${reference}-${alternate}`;
    }
    if (viewBravoUrl) {
      tmpl = (
        <span>
          <i className="varsome-icons icon-info" />
          <a href={viewBravoUrl} target="_blank" data-testid="bravo-url">
            {" View this on Bravo Browser"}
          </a>
        </span>
      );
    }
    return tmpl;
  }

  getTableMobile = (item) => {
    let af;
    let hom;
    if (item.ac === item.het) {
      hom = 0;
    } else {
      hom = (item.ac - item.het) / 2;
    }
    if (item.an !== 0) {
      af = item.ac / item.an;
    }
    let freqStyle;
    if (af) freqStyle = getFrequencyColor(af);
    const tableMobile = (
      <div>
        <dl className="dl-horizontal varsome-dl-horizontal">
          <dt className="col-9 col-xs-9">Allele Count</dt>
          <dd className="col-3 col-xs-3">{item.ac ? item.ac : "-"}</dd>
          <dt className="col-9 col-xs-9">Allele Number</dt>
          <dd className="col-3 col-xs-3">{item.an ? item.an : "-"}</dd>
          <dt className="col-9 col-xs-9">Homozygotes</dt>
          <dd className="col-3 col-xs-3">{hom || "-"}</dd>
          <dt className="col-9 col-xs-9">Allele Frequency</dt>
          <dd className="col-3 col-xs-3">
            {af
              ? getAlleleFrequency(af, 4, freqStyle.color, freqStyle.weight)
              : "-"}
          </dd>
        </dl>
      </div>
    );
    return tableMobile;
  };

  getTableRow = (item) => {
    let af;
    let hom;
    if (item.ac === item.het) {
      hom = 0;
    } else {
      hom = (item.ac - item.het) / 2;
    }
    if (item.an !== 0) {
      af = item.ac / item.an;
    }
    let freqStyle;
    if (af) freqStyle = getFrequencyColor(af);
    return (
      <tr>
        <td>{item.ac ? item.ac : "-"}</td>
        <td>{item.an ? item.an : "-"}</td>
        <td>{hom || "-"}</td>
        <td>
          {af
            ? getAlleleFrequency(af, 4, freqStyle.color, freqStyle.weight)
            : "-"}
        </td>
      </tr>
    );
  };

  render() {
    const { bravo, genome } = this.props;
    if (bravo) {
      const { version } = bravo[0];
      const bravoUrl = getBravoUrl(genome);
      const url = getVarsoUrl(`${bravoUrl}`);
      const titleDetails = bravo[0].main_data;
      const message = (
        <div className="varsome-right">
          <small className="tw-mr-4">
            {bravo[0] && this.getViewBravoUrl()}
          </small>
        </div>
      );
      let filters;
      const tmpl = bravo.map((item) => {
        if (item.filter) {
          filters = item.filter.split(";");
        }
        return (
          <div key={item.id} className="varsome-inner-container">
            {item && (
              <div>
                {item.filter && (
                  <div className="varsome-text">{getFilters(filters)}</div>
                )}
                <MediaQuery minWidth={768}>
                  <table className="varsome-table tw-mt-4">
                    {getTableHeaders()}
                    <tbody>{this.getTableRow(item)}</tbody>
                  </table>
                </MediaQuery>
                <MediaQuery maxWidth={767}>
                  {this.getTableMobile(item)}
                </MediaQuery>
              </div>
            )}
          </div>
        );
      });

      return (
        <div className="varsome-result-fixed" data-testid="bravo-container">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              version={version}
              url={url}
              titleDetailsRight={message}
              helperPopup={<HelperText fieldDescriptionKey="Bravo" />}
              titleDetails={renderMainDataPoint(titleDetails)}
              content={tmpl}
              title="Bravo"
            />
          </div>
        </div>
      );
    }

    return null;
  }
}

Bravo.propTypes = {
  bravo: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
      main_data: PropTypes.string,
      original_variant: PropTypes.string,
    })
  ),
  alternate: PropTypes.string,
  reference: PropTypes.string,
  position: PropTypes.number,
  chromosome: PropTypes.string,
  genome: PropTypes.shape({
    default: PropTypes.bool,
    description: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
};

Bravo.defaultProps = {
  bravo: null,
  alternate: "",
  reference: "",
  position: null,
  chromosome: "",
  genome: null,
};

const mapStateToProps = (state) => {
  return {
    bravo: bravoListSelector(state),
    alternate: getAlt(state),
    reference: getRef(state),
    position: getPosition(state),
    chromosome: getChromosome(state),
    genome: getGenome(state),
  };
};

const mapDispatchToProps = {};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorGuard(Bravo, "Bravo", "Bravo"));
